import { Component, OnInit, HostListener, OnDestroy, Inject, LOCALE_ID } from '@angular/core';
import { KPIService, KPIReturn, HistoryReturn } from 'src/services/kpi/kpi.service';
import { Router } from '@angular/router';
import { LocalStorageService } from 'src/services/local-storage/local-storage.service';
import { SessionStorageService } from 'src/services/session-storage/session-storage.service';
import { ContactService } from 'src/services/contact/contact.service';
import { Contact } from 'src/classes/contact';
import autofillZendesk from 'src/utils/autofillZendesk';
import hoursBetween from 'src/utils/hoursBetween';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ExpirationNoticeModalComponent } from './expiration-notice-modal/expiration-notice-modal.component';

function getLastSunday(date?: Date) {
  if (!date) {
    date = new Date();
  }
  const today = date.getDate();
  const dayOfTheWeek = date.getDay() || 7;
  const newDate = date.setDate(today - dayOfTheWeek);
  return new Date(newDate);
}

interface EventData {
  date: Date;
  successful: number;
  error: number;
  progress: number;
}

interface HistoryReturnWithContact extends HistoryReturn {
  contact: Contact
}

function getContactById(id: number, contacts: Contact[]) {
  return contacts.find(contact => contact.idContact === id);
}

function HistoryReturnToEventData(h: HistoryReturn): EventData {
  return {
    date: h.startDate,
    successful: h.historyType === 'Complete' ? 1 : 0,
    error: h.historyType === 'Error' ? 1 : 0,
    progress: h.historyType === 'Sending' ? 1 : 0,
  };
}

@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss']
})
export class DashboardPageComponent implements OnInit, OnDestroy {

  public noOpenMessage='no open yet';

  private readonly graphSelectionStorageKey = 'default-dashboard-graph-group';

  private state: 'initial-loading' | 'showing-dashboard' | 'reloading-kpi' | 'error' = 'initial-loading';

  public error = {
    title: 'Unknown Error',
    message: 'An unknown error occured',
    stack: 'The dashboard entered the error state incorretly',
    stackExpanded: false,
    options: 'none',
    showSetupButton: false,
  };

  public currentFilterLabel = 'all';
  public currentGroupName: 'daily' | 'monthly' = 'daily';
  public isFilterExpanded = false;
  private intervalId: number;
  private autoUpdateIntervalSeconds = 60;
  public showWelcomeScreen = false;
  private eventData: EventData[] = [];
  public filteredEventData: EventData[] = [];
  private isFirstKpiLoad = true;

  public historyData = {
    errors: 0,
    inProgress: 0,
    success: 0,
    reports: [] as HistoryReturnWithContact[]
  };

  public planData = {
    deliveriesQuantity: '',
    name: '',
    planExpiration: new Date(),
  };

  constructor(
    private kpiService: KPIService,
    private router: Router,
    public modalService: NgbModal,
    private contactService: ContactService,
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  isEnglishLocale() {
    return !(typeof this.locale === 'string' && this.locale.toLowerCase().substring(0, 2) === 'pt');
  }

  ngOnInit() {
    this.refreshDashboardData();
    this.intervalId = window.setInterval(this.silentlyUpdateKpis.bind(this), this.autoUpdateIntervalSeconds * 1000);
    setTimeout(() => autofillZendesk(document.body, this.sessionStorage), 1000);
  }

  ngOnDestroy() {
    window.clearInterval(this.intervalId);
  }

  async silentlyUpdateKpis() {
    if (this.showWelcomeScreen) {
      clearInterval(this.intervalId);
      return;
    }
    this.state = 'reloading-kpi';
    try {
      let list = await Promise.all([
        this.loadKpiData(),
        this.loadHistoryData(),
        this.loadContactData()
      ]);
      if (list === undefined) {
        list = [
          await this.loadKpiData(),
          await this.loadHistoryData(),
          await this.loadContactData()
        ];
      }
      if (list === undefined) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "Dashboard data refresh returned undefined on silent update",
          (new Error()).stack,
          'refresh'
        );
      } else if (!list[0]) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "KPI Data could not be loaded: got " + list[0] + " on silent update",
          (new Error()).stack,
          'refresh'
        );
      } else if (!list[1]) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "History Data could not be loaded: got " + list[1] + " on silent update",
          (new Error()).stack,
          'refresh'
        );
      } else if (!list[2]) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "Contact Data could not be loaded: got " + list[2] + " on silent update",
          (new Error()).stack,
          'refresh'
        );
      }
      return this.onLoadFinish(list[0], list[1], list[2]);
    } catch (err) {
      return this.setErrorState(
        err.title || 'Error Updating Dashboard',
        err.message || 'Missing error message',
        err.stack || '',
        err.options || 'refresh'
      );
    }
  }

  async refreshDashboardData() {
    if(this.isEnglishLocale()){
      this.noOpenMessage='no open yet';
    }
    else{
      this.noOpenMessage='Não foi aberta';
    }
    this.state = 'initial-loading';
    try {
      let list = await Promise.all([
        this.loadKpiData(),
        this.loadHistoryData(),
        this.loadContactData()
      ]);
      if (list === undefined) {
        list = [
          await this.loadKpiData(),
          await this.loadHistoryData(),
          await this.loadContactData()
        ];
      }
      if (list === undefined) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "Dashboard data refresh returned undefined",
          (new Error()).stack,
          'refresh'
        );
      } else if (!list[0]) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "KPI Data could not be loaded: got " + list[0],
          (new Error()).stack,
          'refresh'
        );
      } else if (!list[1]) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "History Data could not be loaded: got " + list[1],
          (new Error()).stack,
          'refresh'
        );
      } else if (!list[2]) {
        return this.setErrorState(
          'Error Loading Dashboard',
          "Contact Data could not be loaded: got " + list[2],
          (new Error()).stack,
          'refresh'
        );
      }
      return this.onLoadFinish(list[0], list[1], list[2]);
    } catch (err) {
      return this.setErrorState(
        err.title || 'Error Loading Dashboard',
        err.message || 'Missing error message',
        err.stack || '',
        err.options || 'refresh'
      );
    }
  }

  onShowAllLogsClick() {
    this.router.navigate(['/logs']);
  }

  onLoadFinish(
    kpiData: any,
    historyEvents: HistoryReturn[],
    contactResponse: {data: Contact[]; total: number;}
  ) {
    do {
      if (!kpiData) {
        console.warn('Missing kpi data');
        break;
      }
      this.planData = kpiData.planData;
    } while (false);

    do {
      if (!historyEvents) {
        console.warn('Missing history data');
        break;
      }

      this.historyData.success = historyEvents.reduce(
        (prev, data) => prev + (data.historyType === 'Complete' ? 1 : 0), 0
      );
      this.historyData.errors = historyEvents.reduce(
        (prev, data) => prev + (data.historyType === 'Error' ? 1 : 0), 0
      );
      this.historyData.inProgress = historyEvents.reduce(
        (prev, data) => prev + (data.historyType === 'Sending' ? 1 : 0), 0
      );
      const contacts = contactResponse.data;
      this.historyData.reports = historyEvents.sort(
        (prev, curr) => curr.startDate === prev.startDate ? 0 : (curr.startDate > prev.startDate ? 1 : -1)
      ).filter(
        report => report.historyType === "Complete" || report.historyType === "Error"
      ).map((report) => {
        console.log(report)
        const contact = getContactById(report.idContact, contacts);
        var lastOpen1= report.lastOpen!=null?`${report.lastOpen.toDateString()} ${String(report.lastOpen.getHours()).padStart(2,"0")}:${String(report.lastOpen.getMinutes()).padStart(2,"0")}`:this.noOpenMessage;
        const countOpen1 = report.countOpen!=null?report.countOpen:'0';
        return Object.assign({contact,lastOpen1,countOpen1}, report);
      });

      this.eventData = historyEvents.map(history => HistoryReturnToEventData(history)).filter(history => history.progress <= 0);

      const defaultGroupFilter = this.localStorage.getItem(this.graphSelectionStorageKey);
      this.onChangeGroupFilterClick(defaultGroupFilter);
    } while (false);

    this.state = 'showing-dashboard';
  }

  onViewSentFilesClick(historyEvent: HistoryReturn) {
    alert('This feature was not implemented yet. idDelivery: ' + historyEvent.idDelivery);
  }

  setErrorState(title: string | null, message: string, stack: any, options: 'none' | 'activate-powerbi' | 'refresh') {
    this.state = 'error';
    if (title !== null) {
      this.error.title = title;
    }
    this.error.message = message;
    this.error.stack = (typeof stack === 'string') ? (
      stack
    ) : (
      stack === null || stack === undefined ? '' : (stack instanceof Error ? stack.toString() : JSON.stringify(stack))
    );
    this.error.options = options;

    this.error.showSetupButton = this.error.message === "Application has not given consent";

    if (this.error.message === "Application has not given consent" && !this.isEnglishLocale()) {
      this.error.message = "A permissão no Azure não foi concedida ao aplicativo";
    }
  }

  async loadContactData() {
    try {
      return await this.contactService.listContacts(null, null, null, true);
    } catch(err) {
      console.log(err);
      throw err;
    }
  }

  async loadHistoryData() {
    try {
      const data = await this.kpiService.getHistory();
      return data;
    } catch(err) {
      console.log(err);
      throw err;
    }
  }

  async loadKpiData() {
    let data: KPIReturn;
    try {
      data = await this.kpiService.getKPIs();
    } catch(err) {
      throw {
        title: 'Failed to load dashboard data',
        message: 'The request to retrieve KPI data caused an error',
        stack: '',
        options: 'refresh'
      };
    }

    if (!data) {
      throw {
        title: 'Failed to load KPI',
        message: 'Service request for KPI data returned an empty object',
        stack: 'Data was of type ' + (typeof data),
        options: 'refresh'
      };
    } else if (data.connection.status === 'Disconnected') {
      this.showWelcomeScreen = true;
    } else if (data.connection.status === 'Error') {
      throw {
        title: 'PowerBI Service Connection Error',
        message: data.connection.message,
        stack: data.connection.details ? data.connection.details : null,
        options: 'refresh'
      };
    }

    if (this.isFirstKpiLoad) {
      this.isFirstKpiLoad = false;
      const hoursUntilExpiration = hoursBetween(data.plan.planExpiration, new Date());
      if (hoursUntilExpiration < 24) {
        let problem: 'long-expired' | 'expired' | 'will-expire';
        if (hoursUntilExpiration < - 24 * 4) {
          problem = 'long-expired';
        } else if (hoursUntilExpiration < 0) {
          problem = 'expired';
        } else { // if (hoursUntilExpiration < 24) {
          problem = 'will-expire';
        }
        this.openExpirationNotice(problem);
      }
    }

    return {
      historyData: {
        errors: data.history.errors || 0,
        inProgress: data.history.inProgress || 0,
        success: data.history.success || 0
      },
      planData: {
        deliveriesQuantity: data.plan.deliveriesQuantity === -1 ? 'Unlimited' : data.plan.deliveriesQuantity.toString(),
        name: data.plan.name,
        planExpiration: data.plan.planExpiration
      }
    };
  }

  async openExpirationNotice(problem: 'long-expired' | 'expired' | 'will-expire') {
    const modal = this.modalService.open(ExpirationNoticeModalComponent);
    modal.componentInstance.problem = problem;

    try {
      const shouldRedirect = await modal.result;
      if (shouldRedirect === true) {
        this.router.navigate(['/billing']);
      }
    } catch(err) {
      // Do nothing, as user must have closed the modal without interacting with it
    }
  }

  onChangeGroupFilterClick(type: string) {
    this.localStorage.setItem(this.graphSelectionStorageKey, type, 30 * 24);
    if (type === 'monthly') {
      this.currentFilterLabel = 'Monthly';
      this.currentGroupName = 'monthly';
      this.filteredEventData = this.eventData;
    } else if (type === 'this-week') {
      this.currentFilterLabel = 'Current week';
      this.currentGroupName = 'daily';
      const weekStart = getLastSunday(new Date());
      this.filteredEventData = this.eventData.filter(event => event.date > weekStart);
    } else if (type === 'this-month') {
      this.currentFilterLabel = 'Current month';
      this.currentGroupName = 'daily';
      const monthStart = new Date((new Date()).toISOString().substring(0, 8) + '01');
      this.filteredEventData = this.eventData.filter(event => event.date >= monthStart);
    } else { // daily
      this.localStorage.setItem(this.graphSelectionStorageKey, 'daily', 30 * 24);
      this.currentFilterLabel = 'All';
      this.currentGroupName = 'daily';
      this.filteredEventData = this.eventData;
    }
  }

  onDropdownExpandClick() {
    this.isFilterExpanded = true;
  }

  // Disables isFilterExpanded
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isFilterExpanded) {
      return;
    }
    const path: EventTarget[] = (event as any).path || event.composedPath();
    for (const element of path) {
      if (!element || !(element instanceof HTMLDivElement) || !element.classList) {
        continue;
      }
      if (element.classList.contains('btn-group') && element.classList.contains('open')) {
        return;
      }
    }
    this.isFilterExpanded = false;
  }

  get isFullPageLoading() { return this.state === 'initial-loading'; }
  get isErrorPage() { return this.state === 'error'; }


  onToggleErrorPress() {
    this.error.stackExpanded = !this.error.stackExpanded;
  }

  onErrorRefreshPress() {
    window.location.reload();
  }

  onRefreshPress() {
    this.refreshDashboardData();
  }

  onActivateRedirectPress() {
    this.router.navigate(['/setup/pbiconnection']);
  }
}
